import { useEffect, useState } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { ClioService, ClioTasksInfo, UsersModel } from "../../api";
import ClioTaskInfoDialog from "./ClioTaskInfoDialog";

const MySwal = withReactContent(Swal);

export default function ClioTaskDataUpdate() {
  const [allTasks, setAllTasks] = useState<ClioTasksInfo[]>([]);
  const [users, setUsers] = useState<UsersModel[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<ClioTasksInfo[]>([]);
  const [selectedTask, setSelectedTask] = useState<ClioTasksInfo | null>(null);
  const [resetDisabled, setResetDisabled] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState("Checking");
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    ClioService.getApiClioTaskData().then((tasks) => {
        const sortedTasks = tasks.sort((t1, t2) => t1.name?.localeCompare(t2.name ?? "") ?? -1);
        setAllTasks(sortedTasks);
        setFilteredTasks(sortedTasks);
    });
    ClioService.getApiClioCheckWebhook().then((status) =>
        setWebhookStatus(status)
    );
    ClioService.getApiClioClioUsers(true).then(clioUsers => setUsers(clioUsers));
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredTasks(allTasks);
      return;
    }
    const lower = searchTerm.toLowerCase();
    setFilteredTasks(
      allTasks.filter(
        (t) =>
          t.name?.toLowerCase().includes(lower) ||
          (t.description ?? "").toLowerCase().includes(lower) ||
          t.assignee?.toLowerCase().includes(lower)
      )
    );
    setPage(0);
  }, [searchTerm, allTasks]);

  const handleUpdate = (updated: ClioTasksInfo) => {
    if (!updated?.id) return;

    ClioService.postApiClioTaskDataUpdate(updated.id, {
      name: updated.name,
      dueDays: updated.dueDays,
      description: updated.description,
      assignee: updated.assignee,
      isCalendarDate: updated.isCalendarDate
    })
      .then(() => {
        setAllTasks((prev) =>
            prev.map((task) => (task.id === updated.id ? updated : task))
        );

        // Also keep filteredTasks in sync
        setFilteredTasks((prev) =>
            prev.map((task) => (task.id === updated.id ? updated : task))
        );
        
        MySwal.fire({
            icon: "success",
            title: <p>Task updated successfully.</p>,
        })
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error updating task.</p>,
        })
      )
      .finally(() => setSelectedTask(null));
  };

  const resetAutomation = () => {
    setResetDisabled(true);
    ClioService.postApiClioResetWebhooks()
      .then(() =>
        MySwal.fire({
          icon: "success",
          title: (
            <p>
              Automation reset. Please wait a minute, then test by changing a
              matter state and completing a task.
            </p>
          ),
        })
      )
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error resetting automation.</p>,
        })
      )
      .finally(() => setResetDisabled(false));
  };

  return (
    <div>
      {/* Header: automation status left, reset right */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          {webhookStatus === "OK" ? (
            <span className="badge fs-5 fw-semibold px-3 py-2 bg-success text-white">
              Automation Status: Working
            </span>
          ) : webhookStatus === "Checking" ? (
            <span className="badge fs-5 fw-semibold px-3 py-2 bg-warning text-dark">
              Automation Status: Checking
            </span>
          ) : (
            <span className="badge fs-5 fw-semibold px-3 py-2 bg-danger text-white">
              Automation Status: Not working
            </span>
          )}
        </div>

        <Button variant="contained" disabled={resetDisabled} onClick={resetAutomation}>
          Reset automation
        </Button>
      </div>

      {/* Search bar */}
      <TextField
        placeholder="Search by name or description..."
        variant="outlined"
        fullWidth
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        className="mb-3"
      />

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><b>ID</b></TableCell>
              <TableCell><b>Name</b></TableCell>
              <TableCell><b>Type</b></TableCell>
              <TableCell><b>Due Days</b></TableCell>
              <TableCell><b>Description</b></TableCell>
              <TableCell><b>Assignee</b></TableCell>
              <TableCell align="right"><b>Action</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((task) => (
                <TableRow key={task.id}>
                  <TableCell>{task.id}</TableCell>
                  <TableCell>{task.name}</TableCell>
                  <TableCell>{task.taskType}</TableCell>
                  <TableCell>{task.dueDays}</TableCell>
                  <TableCell
                    style={{
                      maxWidth: 250,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {task.description}
                  </TableCell>
                  <TableCell>{task.assignee}</TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setSelectedTask(task)}
                    >
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={filteredTasks.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Edit dialog */}
      { selectedTask && <ClioTaskInfoDialog
        task={selectedTask}
        users={users}
        onClose={() => setSelectedTask(null)}
        onSave={handleUpdate}
      /> }
    </div>
  );
}
